import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import { useEffect, useRef, useState } from "react";
import { Map, NavigationControl } from "react-map-gl/mapbox";
import { API_URL, MAPBOX_TOKEN } from "../../config";
import { authFetch } from "../../utils";
import AlerpatHistoryModal from "./AlerpatHistoryModal";
import { PRESET_EMERGENCIAS, PRESET_PREEMERGENCIAS, NIVEL_COLOR, presetLabel } from "./alerpatPresets";

export default function AlerpatPage({ can }) {
  const isAdmin = can ? can("admin") : false;
  const [polygon, setPolygon] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [alertType, setAlertType] = useState("emergencia");
  const [sendToAll, setSendToAll] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [useCustom, setUseCustom] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customText, setCustomText] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [recipientCount, setRecipientCount] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const drawRef = useRef(null);

  const presets = alertType === "emergencia" ? PRESET_EMERGENCIAS : PRESET_PREEMERGENCIAS;
  const accentColor = alertType === "emergencia" ? "#c53030" : "#b45309";
  const isMultiSelect = alertType === "preemergencia";

  const togglePreset = (p) => {
    setUseCustom(false);
    setResult(null);
    if (!isMultiSelect) {
      setSelectedPresets([p]);
      return;
    }
    setSelectedPresets((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  // Poll /alerts for recipient count up to 30 s after sending
  useEffect(() => {
    if (!result?.alertId) return;
    setRecipientCount(null);
    let attempts = 0;
    const id = setInterval(async () => {
      try {
        const res = await authFetch(`${API_URL}alerpat/alerts`);
        const list = await res.json();
        const alert = list.find((a) => a.alertId === result.alertId);
        if (alert != null) setRecipientCount(alert.recipientCount);
      } catch (_) {}
      if (++attempts >= 10) clearInterval(id); // stop after 30 s
    }, 3000);
    return () => clearInterval(id);
  }, [result?.alertId]);

  const handleMapLoad = (map) => {
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: [
        {
          id: "gl-draw-polygon-fill",
          type: "fill",
          filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          paint: { "fill-color": "#e10d19", "fill-opacity": 0.25 },
        },
        {
          id: "gl-draw-polygon-stroke",
          type: "line",
          filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          paint: { "line-color": "#e10d19", "line-width": 2 },
        },
        {
          id: "gl-draw-polygon-midpoint",
          type: "circle",
          filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
          paint: { "circle-radius": 4, "circle-color": "#e10d19" },
        },
        {
          id: "gl-draw-polygon-and-line-vertex",
          type: "circle",
          filter: ["all", ["==", "$type", "Point"], ["==", "meta", "vertex"]],
          paint: {
            "circle-radius": 5,
            "circle-color": "#fff",
            "circle-stroke-color": "#e10d19",
            "circle-stroke-width": 2,
          },
        },
        {
          id: "gl-draw-line",
          type: "line",
          filter: [
            "all",
            ["==", "$type", "LineString"],
            ["!=", "mode", "static"],
          ],
          paint: {
            "line-color": "#e10d19",
            "line-width": 2,
            "line-dasharray": [2, 2],
          },
        },
      ],
    });
    map.addControl(draw);
    drawRef.current = draw;

    map.on("draw.create", (e) => {
      setPolygon(e.features[0].geometry);
      setDrawing(false);
      setResult(null);
    });
    map.on("draw.update", (e) => {
      setPolygon(e.features[0].geometry);
    });
    map.on("draw.delete", () => {
      setPolygon(null);
    });
  };

  const startDrawing = () => {
    if (!drawRef.current) return;
    drawRef.current.deleteAll();
    setPolygon(null);
    setResult(null);
    setRecipientCount(null);
    drawRef.current.changeMode("draw_polygon");
    setDrawing(true);
  };

  const needsPolygon = alertType === "emergencia" && !sendToAll;

  const canSend =
    !sending &&
    (needsPolygon ? !!polygon : true) &&
    (useCustom
      ? customTitle.trim() && customText.trim()
      : selectedPresets.length > 0);

  const handleSend = async () => {
    if (!canSend) return;
    const titulo = useCustom
      ? customTitle.trim()
      : selectedPresets.map((p) => presetLabel(p)).join(" · ");
    const texto = useCustom
      ? customText.trim()
      : selectedPresets.length > 1
        // Prefix each description with its own label only when combining
        // several — with just one selected, the label is already the
        // título, so repeating it here would duplicate it in the message.
        ? selectedPresets.map((p) => `${presetLabel(p)}\n${p.texto}`).join("\n\n")
        : selectedPresets.map((p) => p.texto).join("\n\n");

    const confirmed = window.confirm(
      `¿Seguro que quieres enviar esta ${alertType === "emergencia" ? "EMERGENCIA" : "preemergencia"}?\n\n"${titulo}"`
    );
    if (!confirmed) return;

    setSending(true);
    setResult(null);
    try {
      const res = await authFetch(`${API_URL}alerpat/alert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titulo, texto, polygon: needsPolygon ? polygon : null, type: alertType }),
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: "relative", height: "100%", width: "100%" }}>
      <div style={{ position: "absolute", inset: 0 }}>
        <Map
          initialViewState={{
            longitude: -0.50267,
            latitude: 39.53351,
            zoom: 13,
          }}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
          mapboxAccessToken={MAPBOX_TOKEN}
          onLoad={(e) => handleMapLoad(e.target)}
        >
          <NavigationControl position="top-right" />
        </Map>
      </div>

      <div style={panelStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            AlerPAT — Aviso a la población
          </h3>
          <button
            onClick={() => setShowHistory(true)}
            style={{ border: "1px solid #ccc", borderRadius: 4, background: "#fff", cursor: "pointer", fontSize: 11, padding: "4px 8px" }}
          >
            Histórico
          </button>
        </div>

        {showHistory && <AlerpatHistoryModal onClose={() => setShowHistory(false)} />}

        <div style={{ display: "flex", marginBottom: 12, borderRadius: 4, overflow: "hidden", border: "1px solid #ccc" }}>
          {[
            { value: "preemergencia", label: "Preemergencia" },
            { value: "emergencia", label: "Emergencia" },
          ].map(({ value, label }) => (
            <button
              key={value}
              onClick={() => {
                setAlertType(value);
                setSelectedPresets([]);
                setUseCustom(false);
                setResult(null);
                setSendToAll(false);
                if (drawRef.current) {
                  drawRef.current.deleteAll();
                  setPolygon(null);
                }
              }}
              style={{
                flex: 1,
                padding: "7px 4px",
                fontSize: 12,
                fontWeight: "bold",
                border: "none",
                cursor: "pointer",
                background: alertType === value
                  ? (value === "emergencia" ? "#c53030" : "#b45309")
                  : "#f0f0f0",
                color: alertType === value ? "white" : "#555",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {alertType === "emergencia" && (
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={sendToAll}
              onChange={(e) => {
                const checked = e.target.checked;
                setSendToAll(checked);
                setResult(null);
                if (checked && drawRef.current) {
                  drawRef.current.deleteAll();
                  setPolygon(null);
                  setDrawing(false);
                }
              }}
            />
            Enviar a todos los usuarios, sin restringir por zona
          </label>
        )}

        {needsPolygon && (
          <button onClick={startDrawing} style={drawBtnStyle}>
            {drawing
              ? "Dibujando… (doble clic para terminar)"
              : polygon
                ? "Redibujar polígono"
                : "Dibujar polígono"}
          </button>
        )}

        {(!needsPolygon || polygon) && (
          <div style={{ marginTop: needsPolygon ? 12 : 0 }}>
            <div style={{ fontWeight: "bold", marginBottom: 4, fontSize: 13 }}>
              Mensaje
            </div>
            {isMultiSelect && (
              <div style={{ fontSize: 11, color: "#888", marginBottom: 8 }}>
                Puedes seleccionar varias; el aviso combinará todas las seleccionadas.
              </div>
            )}

            {presets.map((p, i) => (
              <div key={i}>
                {p.categoria && p.categoria !== presets[i - 1]?.categoria && (
                  <div style={categoryHeaderStyle}>{p.categoria}</div>
                )}
                <label style={radioRowStyle}>
                <input
                  type={isMultiSelect ? "checkbox" : "radio"}
                  name="preset"
                  checked={!useCustom && selectedPresets.includes(p)}
                  onChange={() => togglePreset(p)}
                  style={{ marginTop: 2, flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: "bold", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                    {p.icon && <span>{p.icon}</span>}
                    {p.nivel && (
                      <span
                        style={{
                          display: "inline-block",
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: NIVEL_COLOR[p.nivel],
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {p.titulo}
                  </div>
                  {!useCustom && selectedPresets.includes(p) && (
                    <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>
                      {p.texto}
                    </div>
                  )}
                </div>
                </label>
              </div>
            ))}

            <label style={{ ...radioRowStyle, marginTop: 8 }}>
              <input
                type="radio"
                name="preset"
                checked={useCustom}
                onChange={() => {
                  setUseCustom(true);
                  setSelectedPresets([]);
                }}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <span style={{ fontSize: 13, fontWeight: "bold" }}>
                Texto libre
              </span>
            </label>

            {useCustom && (
              <div
                style={{
                  marginTop: 6,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                <input
                  placeholder="Título"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  style={inputStyle}
                />
                <textarea
                  placeholder="Mensaje"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
            )}

            <button
              onClick={handleSend}
              disabled={!canSend}
              style={{
                ...sendBtnStyle,
                background: accentColor,
                opacity: canSend ? 1 : 0.5,
                cursor: canSend ? "pointer" : "not-allowed",
              }}
            >
              {sending
                ? "Enviando…"
                : alertType === "emergencia"
                ? "Enviar emergencia"
                : "Enviar preemergencia"}
            </button>

            {result && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                {result.error ? (
                  <span style={{ color: "#c00" }}>Error: {result.error}</span>
                ) : result.message ? (
                  <span style={{ color: "#555" }}>{result.message}</span>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {isAdmin ? (
                      <div style={{ color: "#555", display: "flex", flexDirection: "column", gap: 2 }}>
                        <span>Usuarios registrados en la app: {result.totalDevices}</span>
                        <span>
                          Envíos exitosos: {result.success}
                          {result.failure > 0
                            ? ` (${result.failure} fallido(s))`
                            : ""}
                          {result.cleaned > 0
                            ? ` · ${result.cleaned} eliminado(s)`
                            : ""}
                        </span>
                      </div>
                    ) : (
                      <span style={{ color: "#555" }}>Envíos exitosos: {result.success}</span>
                    )}
                    {alertType === "emergencia" && (
                      <div style={{ color: "#1a7a1a", fontWeight: "bold" }}>
                        {sendToAll
                          ? (recipientCount == null
                            ? "Confirmaciones recibidas: comprobando…"
                            : `Confirmaciones recibidas: ${recipientCount}`)
                          : (recipientCount == null
                            ? "Usuarios en zona: comprobando…"
                            : `Usuarios en zona: ${recipientCount}`)}
                      </div>
                    )}
                    {isAdmin && result.errorCodes &&
                      Object.keys(result.errorCodes).length > 0 && (
                        <div style={{ color: "#c00", fontSize: 11 }}>
                          {Object.entries(result.errorCodes).map(
                            ([code, count]) => (
                              <div key={code}>
                                {code}: {count}
                              </div>
                            ),
                          )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const panelStyle = {
  position: "absolute",
  top: 10,
  left: 10,
  width: 300,
  maxHeight: "calc(100% - 20px)",
  overflowY: "auto",
  background: "white",
  borderRadius: 8,
  padding: 16,
  zIndex: 10,
  boxShadow: "0 2px 10px rgba(0,0,0,0.3)",
  boxSizing: "border-box",
};

const drawBtnStyle = {
  width: "100%",
  padding: "8px 12px",
  background: "#2c5282",
  color: "white",
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: 13,
};

const radioRowStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  marginBottom: 8,
  cursor: "pointer",
};

const categoryHeaderStyle = {
  fontSize: 11,
  fontWeight: "bold",
  color: "#999",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginTop: 12,
  marginBottom: 4,
};

const inputStyle = {
  width: "100%",
  padding: "6px 8px",
  boxSizing: "border-box",
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: 13,
};

const sendBtnStyle = {
  width: "100%",
  marginTop: 12,
  padding: "9px 12px",
  background: "#c53030",
  color: "white",
  border: "none",
  borderRadius: 4,
  fontSize: 13,
  fontWeight: "bold",
};
